import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import { ArrowLeft, Camera, X, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PostLostFound = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    title: '', description: '', itemType: 'lost', category: '',
    date: '', contactName: '', contactEmail: '', contactPhone: '', location: '',
  });

  useEffect(() => {
    axios.get(`${API}/api/lost-found/categories`).then(res => setCategories(res.data.categories)).catch(() => {});
  }, []);

  const handleChange = (field, value) => { setForm(prev => ({ ...prev, [field]: value })); setError(''); };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (photos.length + files.length > 6) { setError(t('classifieds.maxPhotos', 'Maximum 6 photos allowed')); return; }
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData(); formData.append('file', file);
        const res = await axios.post(`${API}/api/upload/classified-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        setPhotos(prev => [...prev, res.data.url]);
      }
    } catch {
      for (const file of files) {
        const reader = new FileReader();
        reader.onload = (e) => setPhotos(prev => [...prev, e.target.result]);
        reader.readAsDataURL(file);
      }
    } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const validate = () => {
    if (!form.title.trim() || form.title.trim().length < 3) return t('lostFound.errTitle', 'Title must be at least 3 characters');
    if (!form.description.trim() || form.description.trim().length < 10) return t('lostFound.errDesc', 'Description must be at least 10 characters');
    if (!form.category) return t('lostFound.errCategory', 'Please select a category');
    if (!form.contactName.trim()) return t('lostFound.errName', 'Please enter your name');
    if (!form.contactEmail.trim() || !form.contactEmail.includes('@')) return t('lostFound.errEmail', 'Please enter a valid email');
    if (!form.location.trim()) return t('lostFound.errLocation', 'Please enter a location');
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate(); if (v) { setError(v); return; }
    setSubmitting(true); setError('');
    try {
      const payload = {
        title: form.title.trim(), description: form.description.trim(), itemType: form.itemType,
        category: form.category, date: form.date || null,
        photos: photos.filter(p => p.startsWith('http')),
        contactName: form.contactName.trim(), contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim() || null, location: form.location.trim(),
      };
      const res = await axios.post(`${API}/api/lost-found`, payload);
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('lostFound.errSubmit', 'Failed to post. Please try again.'));
    } finally { setSubmitting(false); }
  };

  const categoryIcons = { 'Pets': '🐾', 'Keys': '🔑', 'Wallets/Bags': '👜', 'Documents/ID': '📄', 'Phones/Electronics': '📱', 'Other': '📦' };

  if (success) {
    const manageUrl = `${window.location.origin}/lost-found/${success.id}?token=${success.editToken}`;
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="max-w-lg mx-auto px-4 pt-12">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center border border-gray-100">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2" data-testid="lf-post-success">{t('lostFound.posted', 'Item Posted!')}</h1>
            <p className="text-sm text-gray-600 mb-4">{t('lostFound.postedDesc', 'Your post is now live. It will expire after 30 days.')}</p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-left">
              <p className="text-xs text-amber-800 font-medium mb-1">{t('classifieds.saveLink', 'Save this link to manage your listing:')}</p>
              <p className="text-xs text-amber-700 break-all font-mono">{manageUrl}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate(`/lost-found/${success.id}`)} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 text-sm" data-testid="lf-view-btn">
                {t('lostFound.viewItem', 'View Item')}
              </button>
              <button onClick={() => navigate('/classifieds')} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 text-sm">
                {t('lostFound.browseMore', 'Browse More')}
              </button>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/classifieds')} className="text-gray-600 hover:text-gray-900" data-testid="back-from-lf-post">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900" data-testid="post-lf-title">{t('lostFound.postItem', 'Post Lost or Found Item')}</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Lost / Found toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('lostFound.typeLabel', 'Is this item lost or found?')} *</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => handleChange('itemType', 'lost')}
              className={`flex-1 py-3 rounded-lg text-sm font-semibold border transition-colors ${form.itemType === 'lost' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
              data-testid="lf-type-lost">
              {t('lostFound.iLostSomething', 'I Lost Something')}
            </button>
            <button type="button" onClick={() => handleChange('itemType', 'found')}
              className={`flex-1 py-3 rounded-lg text-sm font-semibold border transition-colors ${form.itemType === 'found' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
              data-testid="lf-type-found">
              {t('lostFound.iFoundSomething', 'I Found Something')}
            </button>
          </div>
        </div>

        {/* Photos */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('classifieds.photos', 'Photos')} ({photos.length}/6)</label>
          <div className="flex gap-2 flex-wrap">
            {photos.map((photo, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                <img src={photo} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                  className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"><X className="h-3 w-3" /></button>
              </div>
            ))}
            {photos.length < 6 && (
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                data-testid="lf-add-photo-btn">
                {uploading ? <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <><Camera className="h-5 w-5" /><span className="text-[10px] mt-0.5">{t('classifieds.addPhoto', 'Add')}</span></>}
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('lostFound.itemTitle', 'What was lost/found?')} *</label>
          <input type="text" value={form.title} onChange={(e) => handleChange('title', e.target.value)}
            placeholder={t('lostFound.titlePlaceholder', 'e.g. Small brown dog, car keys with red tag...')}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            maxLength={120} data-testid="lf-title-input" />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('lostFound.category', 'Category')} *</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {categories.map((cat) => (
              <button key={cat} type="button" onClick={() => handleChange('category', cat)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  form.category === cat ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                }`} data-testid={`lf-post-cat-${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                <span>{categoryIcons[cat] || '📦'}</span><span className="truncate">{t(`lostFound.cat.${cat}`, cat)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {form.itemType === 'lost' ? t('lostFound.dateLost', 'Date lost') : t('lostFound.dateFound', 'Date found')}
            <span className="text-gray-400 font-normal"> ({t('classifieds.optional', 'optional')})</span>
          </label>
          <input type="date" value={form.date} onChange={(e) => handleChange('date', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            data-testid="lf-date-input" />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('classifieds.descLabel', 'Description')} *</label>
          <textarea value={form.description} onChange={(e) => handleChange('description', e.target.value)}
            placeholder={t('lostFound.descPlaceholder', 'Describe the item, where it was lost/found, any identifying features...')}
            rows={4} maxLength={2000}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            data-testid="lf-desc-input" />
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('lostFound.locationLabel', 'Area / Location')} *</label>
          <input type="text" value={form.location} onChange={(e) => handleChange('location', e.target.value)}
            placeholder={t('lostFound.locationPlaceholder', 'e.g. Almería Centro, Retamar beach...')}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            data-testid="lf-location-input" />
        </div>

        {/* Contact */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{t('classifieds.contactInfo', 'Your Contact Details')}</h3>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('classifieds.yourName', 'Name')} *</label>
            <input type="text" value={form.contactName} onChange={(e) => handleChange('contactName', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              data-testid="lf-name-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('classifieds.yourEmail', 'Email')} *</label>
            <input type="email" value={form.contactEmail} onChange={(e) => handleChange('contactEmail', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              data-testid="lf-email-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('classifieds.yourPhone', 'Phone')} <span className="text-gray-400">({t('classifieds.optional', 'optional')})</span></label>
            <input type="tel" value={form.contactPhone} onChange={(e) => handleChange('contactPhone', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              data-testid="lf-phone-input" />
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="lf-post-error">{error}</div>}

        <button type="submit" disabled={submitting}
          className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
          data-testid="lf-submit-btn">
          {submitting ? t('classifieds.posting', 'Posting...') : t('lostFound.postForFree', 'Post for Free')}
        </button>
        <p className="text-center text-xs text-gray-400 pb-4">{t('classifieds.autoExpire', 'Listings automatically expire after 30 days')}</p>
      </form>
      <BottomNavigation />
    </div>
  );
};

export default PostLostFound;
